import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { claimsApi } from '../../services/api';

// Canonical claim status list — mirrors /app/backend/config/claim_statuses.py
// P6 (May 2026 Rhinobill parity): added Fixed, Fix Successful, Partially
// Paid, Denied to match the full Rhinobill lifecycle.
export const STATUSES = [
  'Draft', 'Pending', 'Submitted', 'Accepted',
  'Fixed', 'Fix Successful',
  'Partially Paid', 'Paid',
  'Rejected', 'Denied', 'Voided',
];
export const SELECTABLE_STATUSES = ['Draft', 'Pending', 'Rejected', 'Denied', 'Fixed'];

// Display tone for each status — used for badge colour mapping in the UI.
// Map keys here to Tailwind classes in the ClaimsTableRow / status badge.
export const STATUS_TONE = {
  Draft: 'slate',
  Pending: 'slate',
  Submitted: 'sky',
  Accepted: 'indigo',
  Fixed: 'blue',
  'Fix Successful': 'emerald',
  'Partially Paid': 'amber',
  Paid: 'emerald',
  Rejected: 'rose',
  Denied: 'rose',
  Voided: 'slate',
};

/**
 * Plain-English reason chip inferred from existing claim fields (no extra API
 * call). Returns `{ code, label, tone } | null`. Surfaces ERA adjustments
 * (amber) > 277CA errors (red) > nothing.
 */
export const inferReasonChip = (claim) => {
  if (!claim) return null;
  const adjustments = claim.era_adjustments || [];
  if (adjustments.length > 0) {
    const a = adjustments[0];
    return {
      code: `${a.group_code || ''}-${a.reason_code || ''}`.replace(/^-|-$/g, ''),
      label: a.reason_label || a.reason_description || `Adjusted ${a.amount ? `$${a.amount}` : ''}`.trim(),
      tone: 'amber',
    };
  }
  const errors = claim.edi_277_errors || [];
  if (errors.length > 0) {
    const e = errors[0];
    return {
      code: e.code || claim.edi_277_status_code || '',
      label: e.description || e.message || 'Rejected at intake',
      tone: 'red',
    };
  }
  return null;
};

/**
 * Centralises all data + handler logic for ClaimsPage. Returns the rich
 * state surface the page needs; the page itself stays a thin composition.
 *
 * P34 (Feb 27, 2026): Rhinobill-parity filter rail — adds tab (recent/
 * drafts/issues), 11 server-side filters, saved-views, CSV export, and
 * URL-sync so the biller can paste a filter URL to a colleague.
 */
export function useClaims() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [selectedClaims, setSelectedClaims] = useState([]);

  // P34: Rhinobill-style tabs (Recent / Drafts / Issues). Defaults to
  // recent so the biller sees the full claims-list on page load.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'recent');

  // P34: server-side filter rail. Empty string = no filter applied.
  const [filterPayer, setFilterPayer] = useState(searchParams.get('payer') || '');
  const [filterProcedure, setFilterProcedure] = useState(searchParams.get('proc') || '');
  const [filterServiceFrom, setFilterServiceFrom] = useState(searchParams.get('svc_from') || '');
  const [filterServiceTo, setFilterServiceTo] = useState(searchParams.get('svc_to') || '');
  const [filterClaimNumber, setFilterClaimNumber] = useState(searchParams.get('claim_no') || '');
  const [filterIcn, setFilterIcn] = useState(searchParams.get('icn') || '');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState(searchParams.get('cr_from') || '');
  const [filterCreatedTo, setFilterCreatedTo] = useState(searchParams.get('cr_to') || '');
  const [filterEdi277Category, setFilterEdi277Category] = useState(searchParams.get('277_cat') || '');
  const [filterEdi277Code, setFilterEdi277Code] = useState(searchParams.get('277_code') || '');
  const [filterEdi277Entity, setFilterEdi277Entity] = useState(searchParams.get('277_ent') || '');

  // Filter-options dropdowns + saved views (loaded once)
  const [filterOptions, setFilterOptions] = useState({ payers: [], procedures: [], edi_277: { categories: [], codes: [], entities: [] } });
  const [savedViews, setSavedViews] = useState([]);

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyClaimId, setCopyClaimId] = useState(null);
  const [newServiceDate, setNewServiceDate] = useState('');
  const [copying, setCopying] = useState(false);

  const [odmDialogOpen, setOdmDialogOpen] = useState(false);
  const [triageClaim, setTriageClaim] = useState(null);
  const [triageNotes, setTriageNotes] = useState('');
  const [triaging, setTriaging] = useState(false);
  const [adjudicationClaim, setAdjudicationClaim] = useState(null);

  // Correct / Void dialog state. Stores `{mode, claim}` where claim is the
  // full decrypted doc fetched from the server (so ICD-10 codes show as plain
  // text in the side-by-side comparison, not the `enc:` ciphertext).
  const [correctVoid, setCorrectVoid] = useState(null);
  const [correctVoidLoading, setCorrectVoidLoading] = useState(false);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab && activeTab !== 'recent') params.tab = activeTab;
      // Status filter — ignored when a tab forces a status set.
      if (filterStatus && activeTab === 'recent') params.status = filterStatus;
      if (filterPayer) params.payer_program = filterPayer;
      if (filterProcedure) params.service_code = filterProcedure;
      if (filterServiceFrom) params.start_date = filterServiceFrom;
      if (filterServiceTo) params.end_date = filterServiceTo;
      if (filterClaimNumber) params.claim_number = filterClaimNumber;
      if (filterIcn) params.icn = filterIcn;
      if (filterCreatedFrom) params.created_start = filterCreatedFrom;
      if (filterCreatedTo) params.created_end = filterCreatedTo;
      if (filterEdi277Category) params.edi_277_category = filterEdi277Category;
      if (filterEdi277Code) params.edi_277_code = filterEdi277Code;
      if (filterEdi277Entity) params.edi_277_entity = filterEdi277Entity;
      if (search) params.patient_search = search;
      const response = await claimsApi.getAll(params);
      setClaims(response.data);
    } catch {
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [
    activeTab, filterStatus,
    filterPayer, filterProcedure, filterServiceFrom, filterServiceTo,
    filterClaimNumber, filterIcn, filterCreatedFrom, filterCreatedTo,
    filterEdi277Category, filterEdi277Code, filterEdi277Entity,
    search,
  ]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  // Load dropdown options + saved views once on mount.
  useEffect(() => {
    (async () => {
      try {
        const [opts, views] = await Promise.all([
          claimsApi.filterOptions(),
          claimsApi.listSavedViews(),
        ]);
        setFilterOptions(opts.data || filterOptions);
        setSavedViews(views.data?.views || []);
      } catch {
        // Non-fatal — the rail still works without dropdown choices.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the most-useful filters into URL so bookmarks/share links work.
  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab !== 'recent') next.set('tab', activeTab);
    if (filterStatus) next.set('status', filterStatus);
    if (filterPayer) next.set('payer', filterPayer);
    if (filterProcedure) next.set('proc', filterProcedure);
    if (filterServiceFrom) next.set('svc_from', filterServiceFrom);
    if (filterServiceTo) next.set('svc_to', filterServiceTo);
    if (filterClaimNumber) next.set('claim_no', filterClaimNumber);
    if (filterIcn) next.set('icn', filterIcn);
    if (filterCreatedFrom) next.set('cr_from', filterCreatedFrom);
    if (filterCreatedTo) next.set('cr_to', filterCreatedTo);
    if (filterEdi277Category) next.set('277_cat', filterEdi277Category);
    if (filterEdi277Code) next.set('277_code', filterEdi277Code);
    if (filterEdi277Entity) next.set('277_ent', filterEdi277Entity);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab, filterStatus,
    filterPayer, filterProcedure, filterServiceFrom, filterServiceTo,
    filterClaimNumber, filterIcn, filterCreatedFrom, filterCreatedTo,
    filterEdi277Category, filterEdi277Code, filterEdi277Entity,
  ]);

  // Reset every server-side filter back to its empty state.
  const clearFilters = () => {
    setFilterStatus('');
    setFilterPayer('');
    setFilterProcedure('');
    setFilterServiceFrom('');
    setFilterServiceTo('');
    setFilterClaimNumber('');
    setFilterIcn('');
    setFilterCreatedFrom('');
    setFilterCreatedTo('');
    setFilterEdi277Category('');
    setFilterEdi277Code('');
    setFilterEdi277Entity('');
    setSearch('');
  };

  // Total filters applied (for the rail badge). Includes the patient
  // search box so the Clear control surfaces when only a search term
  // is typed (P34 polish — iter_73 testing agent flagged this gap).
  const activeFilterCount = [
    search,
    filterStatus, filterPayer, filterProcedure, filterServiceFrom, filterServiceTo,
    filterClaimNumber, filterIcn, filterCreatedFrom, filterCreatedTo,
    filterEdi277Category, filterEdi277Code, filterEdi277Entity,
  ].filter(Boolean).length;

  // ── Saved views ────────────────────────────────────────────────
  const saveCurrentView = async (name) => {
    if (!name || !name.trim()) {
      toast.error('Enter a name for this view');
      return;
    }
    try {
      const filters = {
        tab: activeTab, status: filterStatus, payer: filterPayer, proc: filterProcedure,
        svc_from: filterServiceFrom, svc_to: filterServiceTo,
        claim_no: filterClaimNumber, icn: filterIcn,
        cr_from: filterCreatedFrom, cr_to: filterCreatedTo,
        '277_cat': filterEdi277Category, '277_code': filterEdi277Code, '277_ent': filterEdi277Entity,
      };
      const res = await claimsApi.createSavedView({ name: name.trim(), filters });
      setSavedViews((prev) => [res.data, ...prev]);
      toast.success(`Saved "${res.data.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save view');
    }
  };

  const applySavedView = (view) => {
    const f = view.filters || {};
    setActiveTab(f.tab || 'recent');
    setFilterStatus(f.status || '');
    setFilterPayer(f.payer || '');
    setFilterProcedure(f.proc || '');
    setFilterServiceFrom(f.svc_from || '');
    setFilterServiceTo(f.svc_to || '');
    setFilterClaimNumber(f.claim_no || '');
    setFilterIcn(f.icn || '');
    setFilterCreatedFrom(f.cr_from || '');
    setFilterCreatedTo(f.cr_to || '');
    setFilterEdi277Category(f['277_cat'] || '');
    setFilterEdi277Code(f['277_code'] || '');
    setFilterEdi277Entity(f['277_ent'] || '');
    toast.success(`Applied "${view.name}"`);
  };

  const deleteSavedView = async (view) => {
    if (!window.confirm(`Delete saved view "${view.name}"?`)) return;
    try {
      await claimsApi.deleteSavedView(view.id);
      setSavedViews((prev) => prev.filter((v) => v.id !== view.id));
      toast.success('View deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete view');
    }
  };

  // ── CSV export ─────────────────────────────────────────────────
  const exportCsv = async () => {
    try {
      const params = {};
      if (activeTab !== 'recent') params.tab = activeTab;
      if (filterStatus && activeTab === 'recent') params.status = filterStatus;
      if (filterPayer) params.payer_program = filterPayer;
      if (filterProcedure) params.service_code = filterProcedure;
      if (filterServiceFrom) params.start_date = filterServiceFrom;
      if (filterServiceTo) params.end_date = filterServiceTo;
      if (search) params.patient_search = search;
      const res = await claimsApi.exportCsv(params);
      // Trigger browser download
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${claims.length} claims`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  // Server-side search now drives `patient_search`; client-side stays as a
  // free-text catch-all (claim_number + service_code substring) for the
  // results already on screen.
  const filteredClaims = claims.filter((claim) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (claim.patient_name || '').toLowerCase().includes(q)
      || (claim.claim_number || '').toLowerCase().includes(q)
      || (claim.service_code || '').toLowerCase().includes(q)
    );
  });
  const selectableClaims = filteredClaims.filter((c) => SELECTABLE_STATUSES.includes(c.status));

  const handleSelectAll = (checked) =>
    setSelectedClaims(checked ? selectableClaims.map((c) => c.id) : []);
  const handleSelectClaim = (claimId, checked) =>
    setSelectedClaims((prev) => (checked ? [...prev, claimId] : prev.filter((id) => id !== claimId)));

  const handleBatchSubmit = () => {
    // Open the bulk SubmitToOdmDialog with the selected claims. The dialog
    // shows the per-claim Service Line picker so the user can verify or
    // switch each claim's billing line before EDI transmission, then calls
    // /odm-sftp/submit-claims with any overrides.
    if (selectedClaims.length === 0) {
      toast.error('No claims selected');
      return;
    }
    setOdmDialogOpen(true);
  };

  const handleSubmitSingle = (claimId) => {
    // Reuse the same dialog for the single-claim case so the user gets
    // the identical "confirm the service line, then submit" experience.
    setSelectedClaims([claimId]);
    setOdmDialogOpen(true);
  };

  const handleDelete = async (claimId) => {
    if (!window.confirm('Are you sure you want to delete this claim?')) return;
    try {
      await claimsApi.delete(claimId);
      toast.success('Claim deleted');
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete claim');
    }
  };

  const openCopyModal = (claimId) => {
    setCopyClaimId(claimId);
    setNewServiceDate(new Date().toISOString().split('T')[0]);
    setCopyModalOpen(true);
  };

  const handleCopyClaim = async () => {
    if (!newServiceDate) {
      toast.error('Please select a service date');
      return;
    }
    setCopying(true);
    try {
      await claimsApi.copy(copyClaimId, newServiceDate);
      toast.success('Claim copied successfully');
      setCopyModalOpen(false);
      fetchClaims();
    } catch {
      toast.error('Failed to copy claim');
    } finally {
      setCopying(false);
    }
  };

  const openTriage = (claim, action) => {
    setTriageClaim({ id: claim.id, action, claim_number: claim.claim_number });
    setTriageNotes('');
  };

  const handleTriage = async () => {
    if (!triageClaim) return;
    setTriaging(true);
    try {
      const res = await claimsApi.triage(triageClaim.id, triageClaim.action, triageNotes || null);
      const verbs = { fix: 'reopened for editing', void: 'voided', resolve: 'marked as resolved' };
      toast.success(`${res.data.claim_number} ${verbs[triageClaim.action]}`);
      setTriageClaim(null);
      setTriageNotes('');
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Triage failed');
    } finally {
      setTriaging(false);
    }
  };

  // Open the side-by-side Correct / Void dialog. Fetches the full claim doc
  // (decrypted PHI included) so the comparison shows real ICD-10 codes.
  const openCorrectVoid = async (claim, mode) => {
    setCorrectVoidLoading(true);
    try {
      const res = await claimsApi.getById(claim.id);
      setCorrectVoid({ mode, claim: res.data });
    } catch {
      toast.error('Failed to load claim details');
    } finally {
      setCorrectVoidLoading(false);
    }
  };

  const closeCorrectVoid = () => setCorrectVoid(null);

  const handleCorrectVoidSuccess = () => fetchClaims();

  // P6 (May 2026 Rhinobill parity): Mark a Rejected/Denied claim as
  // "Fixed" to track it as in-rework without dropping back to Draft. The
  // next 277CA acceptance will automatically elevate to "Fix Successful".
  const handleMarkFixed = async (claim) => {
    try {
      const res = await claimsApi.markFixed(claim.id);
      toast.success(`${res.data.claim_number} marked as Fixed`);
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark as Fixed');
    }
  };

  return {
    claims, loading, filteredClaims, selectableClaims,
    search, setSearch, filterStatus, setFilterStatus,
    // P34: Rhinobill filter rail surface
    activeTab, setActiveTab,
    filterPayer, setFilterPayer,
    filterProcedure, setFilterProcedure,
    filterServiceFrom, setFilterServiceFrom,
    filterServiceTo, setFilterServiceTo,
    filterClaimNumber, setFilterClaimNumber,
    filterIcn, setFilterIcn,
    filterCreatedFrom, setFilterCreatedFrom,
    filterCreatedTo, setFilterCreatedTo,
    filterEdi277Category, setFilterEdi277Category,
    filterEdi277Code, setFilterEdi277Code,
    filterEdi277Entity, setFilterEdi277Entity,
    filterOptions, savedViews,
    activeFilterCount, clearFilters,
    saveCurrentView, applySavedView, deleteSavedView,
    exportCsv,
    selectedClaims, setSelectedClaims, handleSelectAll, handleSelectClaim,
    handleBatchSubmit, handleSubmitSingle, handleDelete,
    copyModalOpen, setCopyModalOpen, copyClaimId, newServiceDate, setNewServiceDate,
    copying, openCopyModal, handleCopyClaim,
    odmDialogOpen, setOdmDialogOpen,
    triageClaim, setTriageClaim, triageNotes, setTriageNotes, triaging,
    openTriage, handleTriage,
    adjudicationClaim, setAdjudicationClaim,
    correctVoid, correctVoidLoading,
    openCorrect: (claim) => openCorrectVoid(claim, 'correct'),
    openVoid: (claim) => openCorrectVoid(claim, 'void'),
    closeCorrectVoid, handleCorrectVoidSuccess,
    handleMarkFixed,
    fetchClaims,
  };
}
